import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TASK_STATUS, TASK_PRIORITY, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Check, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTableSort } from "@/lib/useTableSort";
import { SignedScanUploadButton } from "@/components/messaging/SignedScanUploadButton";

function ScanTaskAction({ t, onDone }) {
  if (t.task_type !== "signed_scan_pending" || ["done", "cancelled"].includes(t.status) || !t.linked_entity_id) return null;
  return (
    <div className="mt-1.5">
      <SignedScanUploadButton documentId={t.linked_entity_id} onDone={onDone} testId={`task-scan-upload-${t.id}`} />
    </div>
  );
}

export default function Tasks() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("open");
  const [open, setOpen] = useState(false);

  const load = async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    const { data } = await api.get("/tasks", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const setStatus = async (t, s) => {
    await api.patch(`/tasks/${t.id}`, { ...t, status: s });
    load();
  };
  const del = async (t) => {
    if (!await window._domivusConfirm("Aufgabe löschen?")) return;
    await api.delete(`/tasks/${t.id}`);
    toast.success("Gelöscht");
    load();
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Aufgaben</h1>
          <p className="text-sm text-slate-500 mt-1">Aufgaben und Fristen verwalten</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="new-task-btn">
              <Plus size={16} /> Neue Aufgabe
            </Button>
          </DialogTrigger>
          <TaskDialog onSaved={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3">
        <Label className="text-xs text-slate-600">Status:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 h-9" data-testid="task-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            {Object.entries(TASK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto">{items.length} Aufgabe(n)</div>
      </div>

      <TaskTable items={items} setStatus={setStatus} deleteTask={del} today={today} load={load} />
    </div>
  );
}

function TaskTable({ items, setStatus, deleteTask, today, load }) {
  const { sorted, SortableTh } = useTableSort(items, "due_date", "asc", {
    title: (t) => t.title || "",
    priority: (t) => ({ low: 0, medium: 1, high: 2, urgent: 3 }[t.priority] ?? 0),
    status: (t) => t.status || "",
    due_date: (t) => t.due_date || "9999-99-99",
  });
  return (
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {/* Mobile-Karten */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="tasks-mobile-list">
          {sorted.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">Keine Aufgaben.</div>}
          {sorted.map((t) => {
            const overdue = t.due_date && t.due_date < today && !["done", "cancelled"].includes(t.status);
            const s = TASK_STATUS[t.status];
            const p = TASK_PRIORITY[t.priority] || TASK_PRIORITY.medium;
            return (
              <div key={t.id} className={`p-3 flex items-start gap-3 ${overdue ? "bg-rose-50/40" : ""}`} data-testid={`task-card-${t.id}`}>
                <button
                  onClick={() => setStatus(t, t.status === "done" ? "open" : "done")}
                  className={`mt-0.5 w-5 h-5 shrink-0 rounded-sm border ${t.status === "done" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300"} flex items-center justify-center`}
                >
                  {t.status === "done" && <Check size={12} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-900"}`}>{t.title}</div>
                  {t.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>}
                  <ScanTaskAction t={t} onDone={load} />
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.className}`}>{p.label}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.className}`}>{s.label}</span>
                    {t.due_date && (
                      <span className={`text-[10px] tabular-nums ${overdue ? "text-rose-700 font-medium" : "text-slate-500"}`}>· {formatDate(t.due_date)}</span>
                    )}
                  </div>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700 shrink-0" onClick={() => deleteTask(t)}>
                  <Trash size={14} />
                </Button>
              </div>
            );
          })}
        </div>

        <table className="w-full text-sm hidden md:table">
          <thead className="bg-slate-50 text-left">
            <tr>
              <Th className="w-10"></Th>
              <SortableTh field="title">Titel</SortableTh>
              <SortableTh field="priority">Priorität</SortableTh>
              <SortableTh field="status">Status</SortableTh>
              <SortableTh field="due_date">Frist</SortableTh>
              <Th className="w-24 text-right"></Th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Keine Aufgaben.</td></tr>}
            {sorted.map((t) => {
              const overdue = t.due_date && t.due_date < today && !["done", "cancelled"].includes(t.status);
              const s = TASK_STATUS[t.status];
              const p = TASK_PRIORITY[t.priority] || TASK_PRIORITY.medium;
              return (
                <tr key={t.id} className={`border-t border-slate-100 hover:bg-slate-50 ${overdue ? "bg-rose-50/40" : ""}`} data-testid={`task-row-${t.id}`}>
                  <Td>
                    <button
                      onClick={() => setStatus(t, t.status === "done" ? "open" : "done")}
                      className={`w-5 h-5 rounded-sm border ${t.status === "done" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 hover:border-slate-500"} flex items-center justify-center`}
                      data-testid={`task-toggle-${t.id}`}
                    >
                      {t.status === "done" && <Check size={12} />}
                    </button>
                  </Td>
                  <Td>
                    <div className={`font-medium ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-900"}`}>{t.title}</div>
                    {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
                    <ScanTaskAction t={t} onDone={load} />
                  </Td>
                  <Td><span className={`text-[10px] px-2 py-0.5 rounded-full ${p.className}`}>{p.label}</span></Td>
                  <Td><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.className}`}>{s.label}</span></Td>
                  <Td className={`tabular-nums ${overdue ? "text-rose-700 font-medium" : ""}`}>{formatDate(t.due_date)}</Td>
                  <Td className="text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700" onClick={() => deleteTask(t)} data-testid={`task-del-${t.id}`}>
                      <Trash size={14} />
                    </Button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}

function TaskDialog({ onSaved }) {
  const [form, setForm] = useState({
    title: "", description: "", due_date: "",
    priority: "medium", status: "open",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", { ...form, due_date: form.due_date || null });
      toast.success("Aufgabe angelegt");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>Neue Aufgabe</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <F label="Titel"><Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title" /></F>
        <F label="Beschreibung"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Frist"><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></F>
          <F label="Priorität">
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TASK_PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
        </div>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="task-save-btn">Anlegen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function F({ label, children }) {
  return <div><Label className="text-xs text-slate-700">{label}</Label><div className="mt-1">{children}</div></div>;
}
